import { ConfirmationDialog } from "@/components/custom/confirmation-dialog.tsx";
import * as React from "react";

interface ConfirmationConfig {
	title: string;
	description: React.ReactNode;
	confirmText?: string;
	cancelText?: string;
	variant?: "default" | "destructive";
	onConfirm: () => void;
	onCancel?: () => void;
}

interface ConfirmationContextType {
	confirm: (config: ConfirmationConfig) => Promise<boolean>;
}

const ConfirmationContext = React.createContext<ConfirmationContextType | null>(
	null,
);

export function ConfirmationProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [isOpen, setIsOpen] = React.useState(false);
	const [config, setConfig] = React.useState<ConfirmationConfig | null>(null);

	const confirm = React.useCallback(
		(dialogConfig: ConfirmationConfig): Promise<boolean> =>
			new Promise<boolean>((resolve) => {
				const enhancedConfig = {
					...dialogConfig,
					onConfirm: () => {
						dialogConfig.onConfirm();
						setIsOpen(false);
						resolve(true);
					},
					onCancel: () => {
						dialogConfig.onCancel?.();
						setIsOpen(false);
						resolve(false);
					},
				};

				setConfig(enhancedConfig);
				setIsOpen(true);
			}),
		[],
	);

	return (
		<ConfirmationContext.Provider value={{ confirm }}>
			{children}
			{config && (
				<ConfirmationDialog
					{...config}
					open={isOpen}
					onOpenChange={setIsOpen}
				/>
			)}
		</ConfirmationContext.Provider>
	);
}

export function useConfirmationDialog() {
	const context = React.useContext(ConfirmationContext);
	if (!context) {
		throw new Error(
			"useConfirmationDialog must be used within a ConfirmationProvider",
		);
	}
	return context;
}
